import {
  FullWorkingInfos,
  MinimalTimestamp,
  StampType,
  Timestamp,
  TimestampDate,
} from '~/common/entities/Employees/EmployeeTypes';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { DeleteTimespanRequest } from '~/common/entities/Employees/EmployeeRequest';
import { Employee } from '~/common/entities/Department/DepartmentTypes';
import { toApiDateFromDate } from '~/common/utils/timeUtils';
import { IdProvider, unwrap } from '../common/apiCommons';
import settings from '~/common/constants/settings.json';
import { format, set } from 'date-fns';
import moment from 'moment';
import axios from 'axios';
import React from 'react';

const queries = {
  async fetchMinimalTimestamps(ids: IdProvider[], from: Date, to: Date) {
    const res = await axios.post<MinimalTimestamp[]>('/timestamps/', {
      start: from.valueOf(),
      to: to.valueOf(),
      employees: ids.map((idProv) => idProv?.id),
    });
    return unwrap(res);
  },
  async fetchWorkingInfos(ids: IdProvider[], from: Date, to: Date) {
    const res = await axios.post<FullWorkingInfos[]>('/dates/employees/', {
      start: toApiDateFromDate(from),
      end: toApiDateFromDate(to),
      employees: ids.map((idProv) => idProv?.id),
    });
    return unwrap(res);
  },
  async fetchDates(employee: IdProvider, from: Date, to: Date) {
    if (employee?.id === null || employee?.id === undefined) {
      return null;
    }
    const res = await axios.get<TimestampDate[]>(
      `/dates/admin/?start=${toApiDateFromDate(from)}&end=${toApiDateFromDate(
        to,
      )}&employees=${employee.id}`,
    );
    return unwrap(res);
  },
};
const mutations = {
  async deleteTimespan(params: {
    from: Date;
    to: Date;
    request: DeleteTimespanRequest;
  }) {
    const res = await axios.post<void>(
      `/dates/delete/${params.from.valueOf()}/${params.to.valueOf()}/`,
      params.request,
    );
    return unwrap(res);
  },
  async addDate({ employee, date }: { employee: Employee; date: Date }) {
    const res = await axios.post<TimestampDate>('/date/', {
      employee: employee.id,
      date: format(date, settings.dateFnsApiDateFormat),
    });
    return unwrap(res);
  },
  async deleteDate({ date }: { employee: Employee; date: TimestampDate }) {
    const res = await axios.delete<void>(`/date/${date.id}/`);
    return unwrap(res);
  },
  async deleteTimestamp(ts: Timestamp) {
    const res = await axios.delete<TimestampDate>(`/timestamp/edit/${ts.id}/`);
    return unwrap(res);
  },
  editTimestamp({
    date,
    ts,
    start,
    end,
    type,
  }: {
    date: TimestampDate;
    ts: Timestamp;
    start: string;
    end: string;
    type: StampType;
  }) {
    const [startHour, startMin] = start.split(':');
    const [endHour, endMin] = end.split(':');
    const editedStart = set(date.date as unknown as number, {
      minutes: parseInt(startMin!),
      hours: parseInt(startHour!),
    });
    const editedEnd = set(date.date as unknown as number, {
      minutes: parseInt(endMin!),
      hours: parseInt(endHour!),
    });

    const request = {
      start: editedStart.valueOf(),
      end: editedEnd.valueOf(),
      type,
    };
    return axios.put<TimestampDate, { start: number; end: number; type: StampType }>(
      `/timestamp/edit/${ts.id}/`,
      request,
    );
  },
  insertTimestamp({
    date,
    start,
    end,
    stampType,
    employee,
  }: {
    date: TimestampDate;
    start: string;
    end: string;
    stampType: StampType;
    employee: Employee;
  }) {
    const [startHour, startMin] = start.split(':');
    const [endHour, endMin] = end.split(':');
    const editedStart = set(date.date as unknown as number, {
      minutes: parseInt(startMin!),
      hours: parseInt(startHour!),
    });
    const editedEnd = set(date.date as unknown as number, {
      minutes: parseInt(endMin!),
      hours: parseInt(endHour!),
    });

    const request = {
      start: editedStart.valueOf(),
      end: editedEnd.valueOf(),
      type: stampType,
    };

    return axios.post<
      TimestampDate,
      { start: number; end: number; type: StampType }
    >(`/timestamp/insert/${employee.id}/`, request);
  },
};

const hooks = {
  useMinimalTimestamps(
    employees: IdProvider[],
    from: Date,
    to: Date,
    options?: UseQueryOptions<MinimalTimestamp[]>,
  ) {
    return useQuery({
      queryKey: [...(employees ?? []).map((e) => e?.id), 'MinimalTimestamps'],
      queryFn: () => queries.fetchMinimalTimestamps(employees, from, to),
      ...(options ?? {}),
    });
  },
  useWorkingInfos(
    { employees, from, to }: { employees: IdProvider[]; from: Date; to: Date },
    options?: UseQueryOptions<FullWorkingInfos[]>,
  ) {
    return useQuery({
      queryKey: [
        ...(employees ?? []).map((e) => e?.id),
        from,
        to,
        'WorkingInfos',
      ],
      queryFn: () => queries.fetchWorkingInfos(employees, from, to),
      ...(options ?? {}),
    });
  },
  useTotalWorkingTime(
    employees: IdProvider[],
    from: Date,
    to: Date,
    options?: UseQueryOptions<FullWorkingInfos[]>,
  ) {
    const { data: workingInfos } = hooks.useWorkingInfos(
      { employees, from, to },
      options,
    );
    return React.useMemo(() => {
      if (!workingInfos?.length) {
        return 0;
      }
      return workingInfos
        .map((wi) => moment.duration(wi.is_time).asSeconds())
        .reduce((c, d) => c + d, 0);
    }, [workingInfos]);
  },
  useWorkingInfosMap(
    { employees, from, to }: { employees: IdProvider[]; from: Date; to: Date },
    options?: UseQueryOptions<FullWorkingInfos[]>,
  ) {
    const { data: workingInfos } = hooks.useWorkingInfos(
      { employees, from, to },
      options,
    );
    return React.useMemo(() => {
      const map: { [key: number]: FullWorkingInfos } = {};
      for (const info of workingInfos ?? []) {
        map[Number(info.employee)] = info;
      }
      return map;
    }, [workingInfos]);
  },
  useEmployeeDates(
    employee: IdProvider,
    from: Date,
    to: Date,
    options?: UseQueryOptions<TimestampDate[] | null>,
  ) {
    return useQuery({
      queryKey: ['dates', employee?.id, from, to],
      queryFn: () => queries.fetchDates(employee, from, to),
      ...(options ?? {}),
    });
  },
  useDeleteTimespan() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.deleteTimespan,
      async onSuccess() {
        await queryClient.invalidateQueries(['MinimalTimestamps']);
        await queryClient.invalidateQueries(['WorkingInfos']);
      },
    });
  },
  useAddDate() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.addDate,
      async onSuccess(_, params) {
        await queryClient.invalidateQueries(['dates', params.employee.id]);
      },
    });
  },
  useDeleteDate() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.deleteDate,
      async onSuccess(_, params) {
        await queryClient.invalidateQueries(['dates', params.employee.id]);
      },
    });
  },
  useDeleteTimestamp(employee: Employee) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.deleteTimestamp,
      async onSuccess() {
        await queryClient.invalidateQueries(['dates', employee.id]);
      },
    });
  },
  useEditTimestamp(employee: Employee) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: mutations.editTimestamp,
      async onSuccess() {
        await queryClient.invalidateQueries(['dates', employee.id]);
      },
    });
  },
  useInsertTimestamp(employee: Employee) {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (values: {
        date: TimestampDate;
        start: string;
        end: string;
        stampType: StampType;
      }) => mutations.insertTimestamp({ ...values, employee }),

      async onSuccess() {
        await queryClient.invalidateQueries(['dates', employee.id]);
      },
    });
  },
};

const WorkingInfoApi = {
  queries,
  mutations,
  hooks,
};

export default WorkingInfoApi;
